/**
 * AdminSyncStatusPage
 * -------------------
 * Internal ops dashboard at `/admin/sync-status`. Shows both directions
 * of the federation pipeline:
 *
 *   ① JapanETCcard (JEC) → NoJo        (inbound — JEC pushes to us)
 *   ② NoJo → JapanTollReceipts (JTR)   (outbound — we push to JTR)
 *
 * Access model
 * ------------
 * NoJo deliberately ships without a login system. This page is a
 * "hidden URL" — anyone who knows the URL can visit it, but:
 *   • not linked from any public page
 *   • not in sitemap.xml
 *   • `noindex` so search engines won't surface it
 *   • the data it exposes is read-only timestamps + traffic-light
 *     labels with zero PII / business-secret content
 *
 * On first visit we set a localStorage marker so the header's
 * conditional "⚡ SYNC" pill appears in this browser on subsequent
 * page loads — letting the operator navigate back without bookmarking.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Activity,
  AlertCircle,
  ArrowRight,
  RadioTower,
  RefreshCw,
} from "lucide-react";
import SEO from "@/components/SEO";
import { markAdminVisited } from "@/lib/adminVisitMarker";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const STATE_PALETTE = {
  green: { fg: "#136D3A", bg: "#E8F7EE", ring: "#7BD389", Icon: RadioTower, label: "LIVE" },
  amber: { fg: "#7C4A00", bg: "#FEF4E0", ring: "#F5B642", Icon: Activity,   label: "PULL" },
  red:   { fg: "#7A2E2E", bg: "#FBECEC", ring: "#E89595", Icon: AlertCircle, label: "COLD" },
};

function fmtLocal(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false, timeZone: "Asia/Tokyo",
    }).format(new Date(iso));
  } catch { return "—"; }
}

function fmtDuration(sec) {
  if (sec == null) return "—";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s ago`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m ago`;
}

export default function AdminSyncStatusPage() {
  const [data, setData] = useState(null);
  const [errored, setErrored] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);

  // First-visit marker for the header pill. Idempotent — safe to call
  // on every render but we only want it on mount.
  useEffect(() => {
    markAdminVisited();
  }, []);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/testimonials/federation-status`, {
        headers: { Accept: "application/json" },
      });
      if (!r.ok) throw new Error(`http ${r.status}`);
      setData(await r.json());
      setErrored(false);
      setLastFetched(new Date());
    } catch {
      setErrored(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 30 * 1000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const inbound = data ? legView({
    secondsSince: data.seconds_since_webhook,
    fallbackState: data.state,
    fallbackMode: data.mode,
    timestamp: data.last_webhook_at_jst,
    timestampLabel: "Last inbound webhook",
  }) : null;

  const outbound = data ? legView({
    secondsSince: data.seconds_since_sync,
    fallbackState: data.state,
    fallbackMode: data.mode,
    timestamp: data.last_synced_at_jst,
    timestampLabel: "Last NoJo mirror update",
  }) : null;

  return (
    <article className="min-h-screen bg-[#F8F9FA]" data-testid="admin-sync-status-page">
      <SEO title="Sync Status · Admin · NoJo Enterprise" noindex />
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <header className="mb-10 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10.5px] tracking-[0.22em] font-mono-display uppercase font-bold text-[#94A3B8] mb-2">
              Internal ops · Hidden URL · No login required
            </p>
            <h1 className="font-display text-3xl md:text-[36px] font-bold tracking-tight text-[#0F1D2D]">
              Sync status
            </h1>
            <p className="mt-2 text-[13px] text-[#64748B] max-w-2xl">
              Live push-channel health for the data sync pipeline between
              JapanETCcard, NoJo Enterprise, and JapanTollReceipts.
              Auto-refreshes every 30 seconds. Not linked from public pages,
              not in sitemap, not indexed by search engines.
            </p>
          </div>
          <button
            onClick={fetchStatus}
            disabled={loading}
            data-testid="admin-sync-status-refresh"
            className="inline-flex items-center gap-2 rounded-full px-4 h-10 text-[12.5px] font-semibold tracking-tight bg-white border border-[#E2E8F0] hover:border-[#0E5FB0] transition-colors disabled:opacity-50"
            style={{ color: "#0E5FB0" }}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={2.25} />
            Refresh
          </button>
        </header>

        {errored && !data && (
          <p
            data-testid="admin-sync-status-fetch-error"
            className="mb-6 text-[13px] text-[#7A2E2E] bg-[#FBECEC] inline-flex items-center gap-2 rounded-lg px-4 py-3"
          >
            <AlertCircle className="h-4 w-4" strokeWidth={2} />
            Couldn't reach the sync-status endpoint. The page itself works —
            try Refresh in a moment.
          </p>
        )}

        <div className="space-y-6">
          {inbound && (
            <LegCard
              title="JapanETCcard → NoJo"
              subtitle="Inbound · JEC pushes testimonial events to NoJo"
              view={inbound}
              testIdPrefix="admin-sync-inbound"
            />
          )}
          {outbound && (
            <LegCard
              title="NoJo → JapanTollReceipts"
              subtitle="Outbound · NoJo fans events out to JTR"
              view={outbound}
              testIdPrefix="admin-sync-outbound"
            />
          )}
        </div>

        {data?.degraded_reason && (
          <p
            data-testid="admin-sync-status-degraded"
            className="mt-6 text-[12.5px] font-mono-display tracking-[0.04em] text-[#7A2E2E] bg-[#FBECEC] inline-flex items-center gap-2 rounded-lg px-3 py-2"
          >
            <AlertCircle className="h-3.5 w-3.5" strokeWidth={2} />
            degraded: {data.degraded_reason}
          </p>
        )}

        {lastFetched && (
          <p className="mt-8 text-[11.5px] font-mono-display tracking-[0.14em] uppercase text-[#94A3B8]">
            fetched {fmtLocal(lastFetched.toISOString())} JST · refreshes every 30s
          </p>
        )}
      </section>
    </article>
  );
}

function legView({ secondsSince, fallbackState, fallbackMode, timestamp, timestampLabel }) {
  let state = "red";
  let mode = "cold";
  if (secondsSince != null) {
    if (secondsSince <= 30 * 60)      { state = "green"; mode = "live"; }
    else if (secondsSince <= 60 * 60) { state = "amber"; mode = "pull"; }
    else                              { state = "red";   mode = "stale"; }
  } else if (fallbackState) {
    state = fallbackState;
    mode = fallbackMode || "cold";
  }
  return { state, mode, secondsSince, timestamp, timestampLabel };
}

function LegCard({ title, subtitle, view, testIdPrefix }) {
  const palette = STATE_PALETTE[view.state] || STATE_PALETTE.red;
  const Icon = palette.Icon;
  return (
    <div
      data-testid={`${testIdPrefix}-card`}
      data-state={view.state}
      className="rounded-3xl p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5"
      style={{
        backgroundColor: palette.bg,
        boxShadow: `inset 0 0 0 1.5px ${palette.ring}`,
      }}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <Icon className="h-9 w-9 shrink-0" style={{ color: palette.fg }} strokeWidth={2} />
        <div className="min-w-0">
          <div className="text-[10.5px] tracking-[0.22em] font-mono-display uppercase font-bold" style={{ color: palette.fg, opacity: 0.7 }}>
            {subtitle}
          </div>
          <div
            className="text-xl md:text-[24px] font-bold tracking-tight flex items-center gap-2 flex-wrap"
            style={{ color: palette.fg }}
            data-testid={`${testIdPrefix}-title`}
          >
            <span>{title.split(" → ")[0]}</span>
            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            <span>{title.split(" → ")[1]}</span>
          </div>
          <div className="mt-2 text-[12.5px] font-semibold tabular-nums" style={{ color: palette.fg }}>
            {palette.label} · {view.mode.toUpperCase()}
          </div>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[10.5px] tracking-[0.22em] font-mono-display uppercase font-bold" style={{ color: palette.fg, opacity: 0.7 }}>
          {view.timestampLabel}
        </div>
        <div className="text-lg font-semibold tabular-nums" style={{ color: palette.fg }} data-testid={`${testIdPrefix}-timestamp`}>
          {fmtLocal(view.timestamp)} JST
        </div>
        <div className="text-[11.5px] tabular-nums mt-0.5" style={{ color: palette.fg, opacity: 0.75 }}>
          {fmtDuration(view.secondsSince)}
        </div>
      </div>
    </div>
  );
}
